import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    RefreshControl,
    ActivityIndicator,
    StatusBar,
    Image,
    Linking,
    BackHandler,
    Alert,
    Modal,
    FlatList,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import axios from 'axios';
import { Feather } from '@expo/vector-icons';
import { COLORS, API, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';

export default function DashboardScreen({ navigation }) {
    const { user, refreshUser } = useAuth();
    const [stats, setStats] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifModalVisible, setNotifModalVisible] = useState(false);

    const fetchData = async () => {
        try {
            const [statsRes, eventsRes, notifRes] = await Promise.all([
                axios.get(API.MEMBER.STATS(user.id)),
                axios.get(API.EVENT.LIST).catch(() => ({ data: { success: true, data: [] } })),
                axios.get(API.NOTIFICATIONS.LIST(user.id)).catch(() => ({ data: { success: true, data: [], unread_count: 0 } })),
            ]);

            if (statsRes.data.success) setStats(statsRes.data.data);
            if (eventsRes.data.success) setEvents(eventsRes.data.data || []);
            if (notifRes.data.success) {
                setNotifications(notifRes.data.data || []);
                setUnreadCount(notifRes.data.unread_count || 0);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axios.post(API.NOTIFICATIONS.MARK_ALL_READ, { member_id: user.id });
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
        } catch (error) {
            console.error('Mark read error:', error);
        }
    };

    const openNotifications = () => {
        setNotifModalVisible(true);
        if (unreadCount > 0) markAllAsRead();
    };

    useFocusEffect(
        useCallback(() => {
            refreshUser();
            fetchData();

            // Exit confirmation
            const onBackPress = () => {
                Alert.alert(
                    'Keluar Aplikasi',
                    'Yakin ingin keluar dari Rental PS Ming?',
                    [
                        { text: 'Batal', style: 'cancel', onPress: () => { } },
                        { text: 'Ya, Keluar', style: 'destructive', onPress: () => BackHandler.exitApp() }
                    ],
                    { cancelable: false }
                );
                return true;
            };

            const backHandler = BackHandler.addEventListener('hardwareBackPress', onBackPress);

            return () => backHandler.remove();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        refreshUser();
        fetchData();
    };

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const formatCurrency = (num) => {
        return 'MG ' + formatNumber(Math.floor(num || 0));
    };

    const getLevelInfo = (poin) => {
        if (poin >= 500000) return { level: 'LEGENDARY', color: '#FFD700', icon: '👑' };
        if (poin >= 200000) return { level: 'DIAMOND', color: '#00D4FF', icon: '💎' };
        if (poin >= 100000) return { level: 'PLATINUM', color: '#A0AEC0', icon: '🏆' };
        if (poin >= 50000) return { level: 'GOLD', color: '#FFB300', icon: '🥇' };
        if (poin >= 20000) return { level: 'SILVER', color: '#94A3B8', icon: '🥈' };
        return { level: 'BRONZE', color: '#CD7F32', icon: '🥉' };
    };

    const level = getLevelInfo(user?.poin || 0);

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            <ScrollView
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={COLORS.primary}
                        colors={[COLORS.primary]}
                    />
                }
            >
                {/* Modern Header */}
                <View style={styles.header}>
                    <View style={styles.headerContent}>
                        <View style={styles.headerLeft}>
                            <Text style={styles.greeting}>Halo, {user?.nama?.split(' ')[0]}!</Text>
                            <View style={styles.memberIdBox}>
                                <Text style={styles.memberIdLabel}>ID MEMBER:</Text>
                                <Text style={styles.memberIdValue}>{user?.kode_member}</Text>
                            </View>
                        </View>
                        <TouchableOpacity
                            style={styles.notifContainer}
                            onPress={openNotifications}
                        >
                            <View style={styles.notifIcon}>
                                <Feather name="bell" size={20} color={COLORS.textPrimary} />
                                {unreadCount > 0 && (
                                    <View style={styles.notifBadge}>
                                        <Text style={styles.notifBadgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                                    </View>
                                )}
                            </View>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Balance Cards - Red Container with White Cards */}
                <View style={styles.balanceContainer}>
                    <View style={styles.balanceInner}>
                        {/* Saldo Card */}
                        <View style={styles.balanceCard}>
                            <Text style={styles.balanceLabel}>MINGURO</Text>
                            <Text style={styles.balanceValue}>{formatCurrency(user?.saldo || 0)}</Text>
                        </View>

                        {/* Poin Card */}
                        <View style={styles.balanceCard}>
                            <Text style={styles.balanceLabel}>Reward Poin</Text>
                            <Text style={[styles.balanceValue, styles.poinValue]}>{formatNumber(user?.poin)}</Text>
                        </View>
                    </View>
                </View>


                {/* Enhanced Quick Menu */}
                <View style={styles.menuSection}>
                    <Text style={styles.sectionTitle}>Menu Cepat</Text>
                    <View style={styles.menuGrid}>
                        <TouchableOpacity
                            style={styles.menuItem}
                            activeOpacity={0.7}
                            onPress={() => navigation.navigate('Topup')}
                        >
                            <View style={[styles.menuIcon, styles.menuIconPrimary]}>
                                <Feather name="credit-card" size={22} color={COLORS.primary} />
                            </View>
                            <Text style={styles.menuLabel}>Topup</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.menuItem}
                            activeOpacity={0.7}
                            onPress={() => navigation.navigate('Hadiah')}
                        >
                            <View style={[styles.menuIcon, styles.menuIconGold]}>
                                <Feather name="gift" size={22} color="#FFB300" />
                            </View>
                            <Text style={styles.menuLabel}>Hadiah</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.menuItem}
                            activeOpacity={0.7}
                            onPress={() => navigation.navigate('Voucher')}
                        >
                            <View style={[styles.menuIcon, styles.menuIconSecondary]}>
                                <Feather name="tag" size={22} color={COLORS.secondary} />
                            </View>
                            <Text style={styles.menuLabel}>Voucher</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.menuItem}
                            activeOpacity={0.7}
                            onPress={() => navigation.navigate('Riwayat')}
                        >
                            <View style={[styles.menuIcon, styles.menuIconInfo]}>
                                <Feather name="list" size={22} color={COLORS.info} />
                            </View>
                            <Text style={styles.menuLabel}>Riwayat</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Enhanced Info Cards */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionTitle}>Informasi</Text>
                    <View style={styles.infoCard}>
                        <View style={styles.infoIconContainer}>
                            <Feather name="award" size={20} color={COLORS.primary} />
                        </View>
                        <View style={styles.infoContent}>
                            <Text style={styles.infoTitle}>Bayar Cash = Dapat Poin</Text>
                            <Text style={styles.infoText}>Setiap Rp 1 = 1 Poin Reward untuk tukar hadiah</Text>
                        </View>
                    </View>
                    <View style={styles.infoCard}>
                        <View style={styles.infoIconContainer}>
                            <Feather name="smartphone" size={20} color={COLORS.primary} />
                        </View>
                        <View style={styles.infoContent}>
                            <Text style={styles.infoTitle}>Bayar Minguro</Text>
                            <Text style={styles.infoText}>Topup Minguro untuk bayar main PS & belanja</Text>
                        </View>
                    </View>
                    <TouchableOpacity
                        style={[styles.infoCard, styles.infoCardClickable]}
                        activeOpacity={0.8}
                        onPress={() => navigation.navigate('Lokasi')}
                    >
                        <View style={styles.infoIconContainer}>
                            <Feather name="map-pin" size={20} color={COLORS.primary} />
                        </View>
                        <View style={styles.infoContent}>
                            <Text style={styles.infoTitle}>Lokasi Cabang</Text>
                            <Text style={styles.infoText}>2 cabang tersedia • Buka 24 jam</Text>
                        </View>
                        <Feather name="chevron-right" size={20} color={COLORS.textMuted} />
                    </TouchableOpacity>
                </View>

                {/* Enhanced Events Section */}
                <View style={styles.eventsSection}>
                    <Text style={styles.sectionTitle}>🔥 Event & Pengumuman</Text>

                    {events.length === 0 ? (
                        <View style={styles.eventCard}>
                            <View style={styles.eventBanner}>
                                <Text style={styles.eventBannerText}>🎮⚽🏆</Text>
                            </View>
                            <View style={styles.eventContent}>
                                <Text style={styles.eventTitle}>Selamat Datang di Rental PS Ming!</Text>
                                <Text style={styles.eventDesc}>Main PS, kumpulkan poin, tukar hadiah menarik!</Text>
                            </View>
                        </View>
                    ) : (
                        events.map((event, index) => (
                            <View key={index} style={styles.eventCard}>
                                {event.banner && (
                                    <View style={styles.eventImageContainer}>
                                        <Image
                                            source={{ uri: event.banner }}
                                            style={styles.eventImage}
                                        />
                                    </View>
                                )}
                                <View style={styles.eventContent}>
                                    <Text style={styles.eventTitle}>{event.judul}</Text>
                                    <Text style={styles.eventDesc}>{event.deskripsi}</Text>
                                </View>
                            </View>
                        ))
                    )}
                </View>

                <View style={{ height: 100 }} />
            </ScrollView>

            {/* Notification Modal */}
            <Modal
                visible={notifModalVisible}
                animationType="slide"
                transparent={true}
                onRequestClose={() => setNotifModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.notifModal}>
                        <View style={styles.notifModalHeader}>
                            <Text style={styles.notifModalTitle}>🔔 Notifikasi</Text>
                            <TouchableOpacity onPress={() => setNotifModalVisible(false)}>
                                <Feather name="x" size={24} color={COLORS.textPrimary} />
                            </TouchableOpacity>
                        </View>
                        {notifications.length === 0 ? (
                            <View style={styles.notifEmpty}>
                                <Feather name="bell-off" size={48} color={COLORS.textMuted} />
                                <Text style={styles.notifEmptyText}>Belum ada notifikasi</Text>
                            </View>
                        ) : (
                            <FlatList
                                data={notifications}
                                keyExtractor={(item) => item.id.toString()}
                                renderItem={({ item }) => (
                                    <View style={[
                                        styles.notifItem,
                                        item.type === 'winner' && styles.notifItemWinner,
                                        !item.is_read && styles.notifItemUnread
                                    ]}>
                                        <View style={styles.notifItemIcon}>
                                            <Text style={{ fontSize: 24 }}>
                                                {item.type === 'winner' ? '🏆' : item.type === 'success' ? '✅' : 'ℹ️'}
                                            </Text>
                                        </View>
                                        <View style={styles.notifItemContent}>
                                            <Text style={styles.notifItemTitle}>{item.title}</Text>
                                            <Text style={styles.notifItemMessage}>{item.message}</Text>
                                            <Text style={styles.notifItemTime}>
                                                {new Date(item.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                                showsVerticalScrollIndicator={false}
                            />
                        )}
                    </View>
                </View>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    // Compact Header
    header: {
        backgroundColor: COLORS.bgDarker,
        paddingTop: 40,
        paddingBottom: 14,
        paddingHorizontal: 16,
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        shadowColor: COLORS.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 6,
    },
    headerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerLeft: {
        flex: 1,
    },
    greeting: {
        fontSize: 20,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 6,
        letterSpacing: -0.3,
    },
    memberIdBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.primary + '12',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: COLORS.primary + '25',
        alignSelf: 'flex-start',
        gap: 4,
    },
    memberIdLabel: {
        fontSize: 11,
        color: COLORS.primary,
        fontWeight: '600',
    },
    memberIdValue: {
        fontSize: 12,
        color: COLORS.primary,
        fontWeight: '800',
    },
    notifContainer: {
        marginLeft: 12,
    },
    notifIcon: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1.5,
        borderColor: COLORS.borderLight,
    },
    // Balance Container - Red Background
    balanceContainer: {
        marginHorizontal: 16,
        marginTop: 14,
        backgroundColor: COLORS.primary,
        borderRadius: 20,
        padding: 20,
        shadowColor: COLORS.primary,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 16,
        elevation: 8,
    },
    balanceInner: {
        flexDirection: 'row',
        gap: 12,
    },
    balanceCard: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        padding: 16,
    },
    balanceLabel: {
        fontSize: 13,
        color: '#666666',
        fontWeight: '500',
        marginBottom: 8,
    },
    balanceValue: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1A1A1A',
    },
    poinValue: {
        marginLeft: 8,
    },
    // Compact Menu
    menuSection: {
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 10,
        letterSpacing: -0.3,
    },
    menuGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    menuItem: {
        alignItems: 'center',
        width: '22%',
    },
    menuIcon: {
        width: 60,
        height: 60,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 6,
        elevation: 3,
    },
    menuIconPrimary: {
        backgroundColor: COLORS.primary + '15',
    },
    menuIconGold: {
        backgroundColor: '#FFB300' + '15',
    },
    menuIconSecondary: {
        backgroundColor: COLORS.secondary + '15',
    },
    menuIconInfo: {
        backgroundColor: COLORS.info + '15',
    },
    menuLabel: {
        fontSize: 12,
        color: COLORS.textSecondary,
        fontWeight: '600',
    },
    // Compact Info Cards
    infoSection: {
        paddingHorizontal: 16,
        paddingTop: 14,
        gap: 6,
    },
    infoCard: {
        flexDirection: 'row',
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        padding: 16,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
        borderWidth: 1,
        borderColor: COLORS.borderLight,
    },
    infoCardClickable: {
        borderLeftWidth: 4,
        borderLeftColor: COLORS.primary,
    },
    infoIconContainer: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: COLORS.primary + '10',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 14,
    },
    infoContent: {
        flex: 1,
    },
    infoTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    infoText: {
        fontSize: 12,
        color: COLORS.textSecondary,
        lineHeight: 18,
    },
    // Enhanced Events
    eventsSection: {
        paddingHorizontal: 20,
        paddingTop: 32,
    },
    eventCard: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 20,
        overflow: 'hidden',
        marginBottom: 14,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4,
        borderWidth: 1,
        borderColor: COLORS.borderLight,
    },
    eventBanner: {
        height: 120,
        backgroundColor: COLORS.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    eventBannerText: {
        fontSize: 48,
    },
    eventImageContainer: {
        width: '100%',
        aspectRatio: 16 / 9,
        backgroundColor: COLORS.bgDark,
        overflow: 'hidden',
    },
    eventImage: {
        width: '100%',
        height: '100%',
        resizeMode: 'cover',
    },
    eventContent: {
        padding: 18,
    },
    eventTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 6,
    },
    eventDesc: {
        fontSize: 13,
        color: COLORS.textSecondary,
        lineHeight: 20,
    },
    // Notification Styles
    notifBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: COLORS.primary,
        borderRadius: 10,
        minWidth: 18,
        height: 18,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
    },
    notifBadgeText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '700',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    notifModal: {
        backgroundColor: COLORS.bgDarker,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: '80%',
        paddingBottom: 40,
    },
    notifModalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.borderLight,
    },
    notifModalTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    notifEmpty: {
        alignItems: 'center',
        padding: 60,
    },
    notifEmptyText: {
        color: COLORS.textMuted,
        marginTop: 16,
        fontSize: 14,
    },
    notifItem: {
        flexDirection: 'row',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.borderLight,
    },
    notifItemWinner: {
        backgroundColor: 'rgba(255, 215, 0, 0.1)',
        borderLeftWidth: 4,
        borderLeftColor: '#FFD700',
    },
    notifItemUnread: {
        backgroundColor: COLORS.primary + '08',
    },
    notifItemIcon: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    notifItemContent: {
        flex: 1,
    },
    notifItemTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    notifItemMessage: {
        fontSize: 13,
        color: COLORS.textSecondary,
        lineHeight: 18,
        marginBottom: 6,
    },
    notifItemTime: {
        fontSize: 11,
        color: COLORS.textMuted,
    },
});
